'use client';
import { useEffect, useState, useMemo, useContext } from 'react';
import { clsx } from 'clsx';
import { Lightbox } from 'yet-another-react-lightbox';
import type { Slide } from 'yet-another-react-lightbox';
import Inline from 'yet-another-react-lightbox/plugins/inline';
import type { ApiImageProps } from 'components/UI/Image';
import { AppContext } from 'context/AppContext';
import type { GalleryProps, GalleryImage } from 'types/gallery';
import { getAgofCode, SectionMapper } from 'utils/analytics';
import { Icon } from '../Icon';
import styles from './Gallery.module.scss';
import CounterPlugin from './Gallery_counter';
import DotpaginationPlugin from './Gallery_dotpagination';
import SlideInfoPlugin from './Gallery_slideinfo';
import { GalleryButton } from './GalleryButton';
import { GallerySlide } from './GallerySlide';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/captions.css';

declare module 'yet-another-react-lightbox' {
  interface SlideImage {
    src: string;
    title?: string;
    image?: ApiImageProps;
    count?: number;
    fields?: {
      [key: string]: string | object | Array<object>;
    };
    loading?: boolean;
    adslot?: boolean;
  }
}

export const Gallery = ({ inlineGalleryItems, galleryInfo, trackOnInteraction }: GalleryProps) => {
  const {
    analyticsData: { ivwCode, homeSectionPath },
    pageMetaData: { adZone, keywords },
  } = useContext(AppContext);

  const sections = homeSectionPath && SectionMapper(homeSectionPath);
  const agof = sections && getAgofCode(sections);

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [lastTrackedIndex, setLastTrackedIndex] = useState<number | null>(null);

  const adReplay = 3; // replay ad after x images

  const slides: Array<Slide> = useMemo(() => {
    const galleryItemsWithAds = inlineGalleryItems.reduce((result: Array<GalleryImage | 'ad-marker'>, item, i) => {
      const itemWithCount = { ...item, count: i + 1, totalAmount: inlineGalleryItems.length };
      result.push(itemWithCount);
      if ((i + 1) % adReplay === 0 && i !== inlineGalleryItems.length - 1) {
        result.push('ad-marker');
      }
      return result;
    }, []);

    return inlineGalleryItems.length > 0
      ? galleryItemsWithAds.map((item) =>
          item === 'ad-marker'
            ? {
                src: '', // src is required, but won't be used
                adslot: true,
              }
            : {
                src: item.model.image.defaultUrl,
                title: item.model.title,
                image: item.model.image,
                fields: item.model.fields,
                count: item.count,
                totalAmount: item.totalAmount,
              }
        )
      : ([
          {
            loading: true,
          },
        ] as Array<Slide>);
  }, [inlineGalleryItems, adReplay]);

  const openGallery = () => {
    setOpen(true);
  };

  const closeGallery = () => {
    setOpen(false);
  };

  const updateIndex =
    (when: boolean) =>
    ({ index: current }: { index: number }) => {
      if (when === open) {
        setCurrentIndex(current);
      }
    };

  const openGalleryButton = () => (
    <button key="open-gallery" type="button" className={styles.button} onClick={openGallery} aria-label="Open Gallery">
      <Icon className={styles['gallery-icon']} name="fullscreenEnter" />
    </button>
  );

  const closeGalleryButton = () => (
    <button
      key="close-gallery"
      type="button"
      className={styles.button}
      onClick={closeGallery}
      aria-label="Close Gallery"
    >
      <Icon className={styles['gallery-icon']} name="fullscreenExit" />
    </button>
  );

  useEffect(() => {
    const trackGallery = (currentIndex: number) => {
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      //find current slide
      const length = slides.filter((slide) => !slide.adslot).length;
      const currentSlide = slides[currentIndex];

      if (!currentSlide.adslot) {
        window.dataLayer.push({
          event: 'gallery_page_view',
          gallery_content: {
            current_picture: currentSlide.count,
            current_picture_id: currentSlide.image?.id ?? '',
            current_picture_url: currentSlide.image?.defaultUrl ?? '',
            number_of_pictures: length,
            first_headline: galleryInfo?.model.fields?.['seo-title'] ?? '',
            headline: galleryInfo?.model.title ?? '',
            url: galleryInfo?.model.url ?? '',
            id: galleryInfo?.id ?? '',
            main_section: galleryInfo?.model.homeSection ?? '',
            tags: galleryInfo?.model['tags']?.map((tag) => tag.name) ?? [],
            first_publication_datetime: galleryInfo?.model.publishedAt,
            last_update_datetime: galleryInfo?.model.lastmodifiedAt,
            editorial_unit: galleryInfo?.model.fields?.ressort ?? '',
            view_type: open ? 'fullscreen' : 'inline',
          },
        });
        setLastTrackedIndex(currentIndex);
      }
    };

    //if "trackOnInteraction" is false, track every index change but start at 2nd slide to avoid double tracking on open
    if (!trackOnInteraction) {
      if (currentIndex > 0 && currentIndex !== lastTrackedIndex) {
        trackGallery(currentIndex);
      }
      return;
    }
    // Track when gallery is opened in fullscreen || Track when user navigates to second slide in inline mode
    if ((open && !hasInteracted) || (!open && currentIndex > 0 && !hasInteracted)) {
      setHasInteracted(true);
      // Track all slides up to current index when first opened
      for (let i = 0; i <= currentIndex; i++) {
        trackGallery(i);
      }
    }
    // Continue tracking after interaction has occurred, only if index changed
    else if (hasInteracted && currentIndex !== lastTrackedIndex) {
      trackGallery(currentIndex);
    }
  }, [
    currentIndex,
    open,
    hasInteracted,
    lastTrackedIndex,
    slides,
    galleryInfo,
    keywords,
    adZone,
    agof,
    ivwCode,
    trackOnInteraction,
  ]);

  return (
    <>
      {inlineGalleryItems && inlineGalleryItems.length > 0 && (
        <Lightbox
          className={clsx(styles['lightbox'], styles['inline'])}
          open={open}
          index={currentIndex}
          on={{
            view: updateIndex(false),
          }}
          slides={slides}
          styles={{
            slide: { padding: 0 },
            button: { filter: 'none' },
            toolbar: { padding: '0' },
          }}
          render={{
            slide: (props) => {
              const { slide } = props;
              return (
                <GallerySlide slide={slide} className={styles.inline} isFullscreen={false} fullscreenOpen={open} />
              );
            },
            buttonPrev: () => {
              return <GalleryButton variant="prev" className={styles.inline} />;
            },
            buttonNext: () => {
              return <GalleryButton variant="next" className={styles.inline} />;
            },
          }}
          toolbar={{
            buttons: [openGalleryButton()],
          }}
          plugins={[Inline, CounterPlugin, DotpaginationPlugin, SlideInfoPlugin({ isInline: true })]}
          carousel={{
            finite: true,
            padding: 0,
            spacing: 0,
            preload: slides.length,
          }}
        />
      )}

      <Lightbox
        className={styles['lightbox']}
        index={currentIndex}
        open={open}
        on={{
          view: updateIndex(true),
        }}
        close={() => {
          closeGallery();
          setOpen(false);
        }}
        slides={slides}
        styles={{
          slide: { padding: 0 },
          button: { filter: 'none' },
          toolbar: { padding: '0' },
        }}
        render={{
          slide: (props) => {
            const { slide } = props;
            return <GallerySlide slide={slide} isFullscreen fullscreenOpen={open} />;
          },
          buttonPrev: () => {
            return <GalleryButton variant="prev" />;
          },
          buttonNext: () => {
            return <GalleryButton variant="next" />;
          },
        }}
        toolbar={{
          buttons: [closeGalleryButton()],
        }}
        plugins={[CounterPlugin, SlideInfoPlugin({ isInline: false })]}
        carousel={{
          finite: true,
          preload: slides.length,
        }}
      />
    </>
  );
};
